import { AttachMoney, Cancel as CancelIcon, CheckCircle, Close, HourglassEmpty, ReceiptLong, Visibility, EmojiEvents } from "@mui/icons-material";
import { alpha, Box, Chip, Grid, IconButton, Paper, Stack, Tooltip, Typography, useTheme } from "@mui/material"; // Importamos 'alpha'
import { format } from "date-fns";
import { ComissionStatusData } from "../../../../../shared/services/api/comission/ComissionServices";
import { globalFormatCurrencyBRL } from "../../../../../shared/utils";

// Interface ComissionRowData (Mantida, pois está correta após o último ajuste)
interface ComissionRowData extends Omit<ComissionStatusData, "dueDate" | "createdAt"> {
	createdAt: string | null;
	dueDate: string | null;
	description: string;
	userBonus: string | null;
	proposal: { certificateNumber: number };
}

interface Props {
	row: ComissionRowData;
	canViewDetails: boolean;
	canCancel: boolean;
	onCancel: (id: string) => void;
	onView: (row: ComissionRowData) => void;
}

export const ComissionCard = ({ row, canViewDetails, canCancel, onCancel, onView }: Props) => {
	const theme = useTheme();
	const isDarkMode = theme.palette.mode === "dark";
	const statusColors = {
		paid: {
			bg: isDarkMode ? alpha(theme.palette.success.main, 0.1) : "#f0fdf4",
			color: theme.palette.success.main,
			icon: <CheckCircle />,
		},
		preview: {
			bg: isDarkMode ? alpha(theme.palette.warning.main, 0.1) : "#fffbe6",
			color: theme.palette.warning.main,
			icon: <HourglassEmpty />,
		},
		canceled: {
			bg: isDarkMode ? alpha(theme.palette.error.main, 0.1) : "#fef2f2",
			color: theme.palette.error.main,
			icon: <CancelIcon />,
		},
		billed: {
			bg: isDarkMode ? alpha(theme.palette.info.main, 0.1) : "#eef2ff",
			color: theme.palette.info.main,
			icon: <ReceiptLong />,
		},
		transferred: {
			bg: isDarkMode ? alpha(theme.palette.info.dark, 0.1) : "#e6fffa",
			color: theme.palette.info.dark,
			icon: <AttachMoney />,
		},
	};

	const currentStatus = statusColors[row.status.toLowerCase() as keyof typeof statusColors] || {
		bg: isDarkMode ? theme.palette.grey[800] : theme.palette.grey[50],
		color: theme.palette.grey[500],
		icon: <Visibility />,
	};

	const renderStatusChip = () => {
		if (!row.status) return <Box sx={{ width: 70, height: 24 }} />;

		const statusKey = row.status.toLowerCase();

		const iconMap = {
			paid: <CheckCircle fontSize="small" sx={{ color: currentStatus.color }} />,
			preview: <HourglassEmpty fontSize="small" sx={{ color: currentStatus.color }} />,
			canceled: <CancelIcon fontSize="small" sx={{ color: currentStatus.color }} />,
			billed: <ReceiptLong fontSize="small" sx={{ color: currentStatus.color }} />,
			transferred: <AttachMoney fontSize="small" sx={{ color: currentStatus.color }} />,
		};

		const labelMap = {
			paid: "Pago",
			preview: "Prévia",
			canceled: "Cancelado",
			billed: "Faturado",
			transferred: "Transferido",
		};

		return (
			<Chip
				label={
					<Stack direction="row" alignItems="center" gap={0.5}>
						{iconMap[statusKey as keyof typeof iconMap] || <Visibility fontSize="small" sx={{ color: currentStatus.color }} />}
						{labelMap[statusKey as keyof typeof labelMap] || row.status}
					</Stack>
				}
				size="small"
				sx={{
					fontWeight: 400,
					borderRadius: 2,
					backgroundColor: currentStatus.color + "22",
					color: currentStatus.color,
				}}
			/>
		);
	};

	// 2. Estilo do Botão de Ação (Adaptado para Dark Mode)
	const actionButtonStyle = {
		backgroundColor: isDarkMode ? alpha(theme.palette.common.white, 0.08) : theme.palette.grey[100],
		"&:hover": {
			backgroundColor: isDarkMode ? alpha(theme.palette.common.white, 0.15) : theme.palette.grey[200],
		},
		borderRadius: 1.5,
		p: 0.5,
	};

	return (
		<Paper
			sx={{
				px: 2,
				py: 1,
				mb: 2,
				width: "100%",
				backgroundColor: currentStatus?.bg || theme.palette.background.paper,
				borderLeft: `4px solid ${currentStatus?.color || theme.palette.grey[500]}`,
				display: "flex",
				flexDirection: "column",
				gap: 1,
				boxShadow: isDarkMode ? "none" : "rgba(0, 0, 0, 0.1) 0px 0px 5px 0px, rgba(0, 0, 0, 0.1) 0px 0px 1px 0px",
				borderRadius: 2,
			}}
		>
			{/* Título: Tipo de Comissionamento e Nº da Apólice/Certificado */}
			<Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ minHeight: 32 }}>
				<Stack direction="row" alignItems="center" gap={1}>
					<Typography fontWeight={600}>
						<Box component="span">{row.type.toLowerCase() === "prize" ? "Premiação" : row.type.toLowerCase() === "bonus" ? "Bônus" : row.type}</Box>
					</Typography>
					{row.type?.toLowerCase() === "prize" && row.userBonus && parseFloat(row.userBonus) > 0 && (
						<Tooltip title="Repasse sobre Premiação">
							<Chip
								size="small"
								icon={<EmojiEvents fontSize="small" />}
								label={`Repasse: ${globalFormatCurrencyBRL(row.userBonus)}`}
								sx={{
									backgroundColor: alpha(theme.palette.info.main, 0.15),
									color: theme.palette.info.main,
									fontWeight: 500,
									fontSize: "0.7rem",
									height: 22,
									"& .MuiChip-icon": { fontSize: "0.85rem", color: theme.palette.info.main },
								}}
							/>
						</Tooltip>
					)}
				</Stack>
				{renderStatusChip()}
			</Stack>

			{/* Informações alinhadas em Grid */}
			<Grid container alignItems="center" spacing={2}>
				{/* Descrição / Provento */}
				<Grid item xs={12} sm={4}>
					<Stack direction="row" alignItems="center" gap={1}>
						<ReceiptLong fontSize="small" sx={{ color: theme.palette.text.secondary }} />
						<Typography variant="body2" color="text.secondary">
							{row.description}
						</Typography>
					</Stack>
				</Grid>

				{/* Vencimento (Ou Data de Referência/Criação) */}
				<Grid item xs={12} sm={3}>
					<Typography variant="body2">
						{row.dueDate ? "Vencimento" : "Criação"}:
						<strong style={{ marginLeft: 4 }}>{format(new Date(row.dueDate || row.createdAt || new Date()), "dd/MM/yyyy")}</strong>
					</Typography>
				</Grid>

				{/* Valor da Comissão */}
				<Grid item xs={12} sm={3} textAlign={"right"}>
					<Typography variant="body2">
						Valor:
						<strong
							style={{
								// Cores de valor adaptadas
								color: parseFloat(row.value) < 0 ? theme.palette.error.main : theme.palette.success.dark,
							}}
						>
							{globalFormatCurrencyBRL(row.value)}
						</strong>
					</Typography>
				</Grid>

				{/* Ações */}
				<Grid item xs={12} sm={2}>
					<Stack direction="row" justifyContent="flex-end" gap={1}>
						{canViewDetails && (
							<Tooltip title="Visualizar detalhes">
								<IconButton size="small" onClick={() => onView(row)} sx={actionButtonStyle}>
									<Visibility fontSize="small" />
								</IconButton>
							</Tooltip>
						)}

						{/* Ação de Cancelar - Só disponível se for Super Admin e status for "preview" ou "pending" */}
						{canCancel && (row.status.toLowerCase() === "preview" || row.status.toLowerCase() === "pending") && (
							<Tooltip title="Cancelar comissão">
								<IconButton size="small" onClick={() => onCancel(row.id)} sx={actionButtonStyle}>
									<Close fontSize="small" sx={{ color: theme.palette.error.main }} />
								</IconButton>
							</Tooltip>
						)}
					</Stack>
				</Grid>
			</Grid>
		</Paper>
	);
};
